import { useState } from 'react';
import apiFetch from '../../utils/apiFetch';
import { authHeaders } from './helpers';
import type { CreatedEmployee, CreateEmployeePayload, EmployeeForm } from './types';

export function buildPayload(form: EmployeeForm): CreateEmployeePayload {
  const payload: CreateEmployeePayload = {
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    status: 'active',
  };
  if (form.email.trim()) payload.personal_email = form.email.trim();
  if (form.mobile.trim()) payload.mobile_phone = form.mobile.trim();
  if (form.role.trim()) payload.position_title = form.role.trim();
  if (form.hireDate) payload.start_date = form.hireDate;
  return payload;
}

export function useAddEmployee() {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdEmployee, setCreatedEmployee] = useState<CreatedEmployee | null>(null);

  const submit = async (form: EmployeeForm): Promise<CreatedEmployee | null> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildPayload(form);
      const res = await apiFetch('/api/v1/payroll/employees', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as any));
        const msg = typeof body.detail === 'string'
          ? body.detail
          : Array.isArray(body.detail)
            ? body.detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
            : 'Could not create employee (status ' + res.status + ').';
        throw new Error(msg);
      }
      const data: CreatedEmployee = await res.json();
      setCreatedEmployee(data);
      return data;
    } catch (err: any) {
      setSubmitError(err.message);
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const reset = (): void => {
    setSubmitError(null);
    setCreatedEmployee(null);
  };

  return { submit, submitting, submitError, createdEmployee, reset };
}